import { z } from "zod"

import type { GenerativeSchedule, NotebookCatalog } from "./notebookSeries"
import {
  createReportRequest,
  ReportRequestError,
  saveNotebookRevision,
  unpackNotebook,
} from "./reportClient"
import { latestNotebookRevision } from "./reportRecipe"
import type { Notebook } from "./reportTypes"

/** The page that creates notebooks and opens the ones it generated. */
export const NOTEBOOK_BUILDER_PATH = "/home/notebooks/new"

const GENERATED_NOTEBOOK_REFUSAL_STATUSES: ReadonlySet<number> = new Set([404])
const generativeSchedulesSchema = z.array(
  z.object({
    id: z.string().min(1),
    name: z.string(),
    cadence: z.string().min(1),
    enabled: z.boolean(),
    template_notebook_id: z.string().min(1),
    edition_ids: z
      .array(z.string().min(1))
      .refine((ids) => new Set(ids).size === ids.length),
    created_at: z.string().nullish(),
    updated_at: z.string().nullish(),
    execution_mode: z.enum(["manual", "automatic"]).nullish(),
    status: z.string().nullish(),
    next_run_at: z.string().nullish(),
    last_run: z
      .object({
        status: z.string(),
        at: z.string().nullish(),
        notebook_id: z.string().nullish(),
        error: z.string().nullish(),
        period_end: z.string().nullish(),
      })
      .nullish(),
  }),
)

/**
 * The one refusal that means "nothing to show": a Sofia deployment without the
 * list endpoint (404). Anything else, including a 401 from a stale session or
 * a 403 access denial, is worth reporting.
 */
export function isGeneratedNotebooksRefusal(failure: unknown): boolean {
  return (
    failure instanceof ReportRequestError &&
    GENERATED_NOTEBOOK_REFUSAL_STATUSES.has(failure.status)
  )
}

/**
 * Renames a generated notebook the way the builder saves one: a full revision
 * of the latest document, guarded by that revision.
 */
export async function renameGeneratedNotebook(
  organizationSlug: string,
  workspaceId: string,
  notebookId: string,
  title: string,
): Promise<Notebook> {
  const request = createReportRequest(organizationSlug, workspaceId)
  const path = `/api/notebooks/${encodeURIComponent(notebookId)}`
  const latest = unpackNotebook(
    await request<Notebook | { notebook: Notebook }>(path, undefined, "GET"),
  )
  return saveNotebookRevision(
    request,
    { ...latest, title },
    latestNotebookRevision(latest),
  )
}

export async function listNotebookCatalog(
  organizationSlug: string,
  workspaceId: string,
  signal?: AbortSignal,
): Promise<NotebookCatalog> {
  const payload = await createReportRequest(
    organizationSlug,
    workspaceId,
  )<NotebookCatalog>("/api/notebooks", undefined, "GET", signal)
  if (payload.generative_schedules === undefined) {
    return { ...payload, notebooks: payload.notebooks ?? [] }
  }
  const parsed = generativeSchedulesSchema.safeParse(
    payload.generative_schedules,
  )
  if (!parsed.success) {
    // Keep the existing report catalog usable while refusing to guess at lineage.
    return {
      ...payload,
      notebooks: payload.notebooks ?? [],
      generative_schedules: undefined,
    }
  }
  const generative_schedules: GenerativeSchedule[] = parsed.data.map(
    (schedule) => ({
      id: schedule.id,
      name: schedule.name,
      cadence: schedule.cadence as GenerativeSchedule["cadence"],
      enabled: schedule.enabled,
      template_notebook_id: schedule.template_notebook_id,
      edition_ids: schedule.edition_ids,
      ...(schedule.created_at ? { created_at: schedule.created_at } : {}),
      ...(schedule.updated_at ? { updated_at: schedule.updated_at } : {}),
      ...(schedule.execution_mode
        ? { execution_mode: schedule.execution_mode }
        : {}),
      ...(schedule.status ? { status: schedule.status } : {}),
      ...(schedule.next_run_at ? { next_run_at: schedule.next_run_at } : {}),
      ...(schedule.last_run
        ? {
            last_run: {
              status: schedule.last_run.status,
              ...(schedule.last_run.at ? { at: schedule.last_run.at } : {}),
              ...(schedule.last_run.notebook_id
                ? { notebook_id: schedule.last_run.notebook_id }
                : {}),
              ...(schedule.last_run.error
                ? { error: schedule.last_run.error }
                : {}),
              ...(schedule.last_run.period_end
                ? { period_end: schedule.last_run.period_end }
                : {}),
            },
          }
        : {}),
    }),
  )
  return {
    ...payload,
    notebooks: payload.notebooks ?? [],
    generative_schedules,
  }
}
